import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/cn';

/**
 * Input component variants configuration
 * 
 * Defines the visual variants and size options for the Input component
 * using class-variance-authority for type-safe styling.
 */

const inputVariants = cva(
  'block w-full rounded-lg bg-[var(--color-surface-bg)] px-3 py-2 text-[var(--color-surface-text)] placeholder-[var(--color-surface-text)] placeholder-opacity-60 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)] disabled:cursor-not-allowed disabled:bg-[var(--color-surface-bg)] disabled:text-[var(--color-surface-text)] disabled:opacity-50 border border-[var(--color-border)]',
  {
    variants: {
      inputSize: {
        sm: 'h-8 px-2 text-sm',
        md: 'h-10 px-3 text-sm',
        lg: 'h-12 px-4 text-base',
      },
      error: {
        true: 'border-[var(--color-error)] focus:ring-[var(--color-error)]',
        false: '',
      },
    },
    defaultVariants: {
      inputSize: 'md',
      error: false,
    },
  }
);

/**
 * Input component props interface
 * 
 * Extends HTML input attributes and includes input-specific props
 * for styling, validation, and icon placement.
 */
export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {
  /** Optional label text to display above the input */
  label?: string;
  /** Error message to display below the input */
  errorMessage?: string;
  /** Icon to display on the left side of the input */
  leftIcon?: React.ReactNode;
  /** Icon to display on the right side of the input */
  rightIcon?: React.ReactNode;
}

/**
 * Input component
 * 
 * A versatile input component that provides consistent styling and behavior
 * across the application. Supports multiple sizes, validation states,
 * and icon placement.
 * 
 * @example
 * ```tsx
 * <Input 
 *   label="Email" 
 *   placeholder="Enter your email"
 *   errorMessage={errors.email}
 *   leftIcon={<EmailIcon />}
 * />
 * ```
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, inputSize, error, errorMessage, label, leftIcon, rightIcon, id, ...props }, ref) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className="w-full">
        <div className="flex items-center gap-3">
          {label && (
            <label htmlFor={inputId} className="text-sm font-medium text-[var(--color-surface-text)] min-w-0 flex-shrink-0">
              {label}:
            </label>
          )}
          <div className="relative flex-1">
            {leftIcon && (
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <div className="h-5 w-5 text-[var(--color-surface-text)] opacity-50 flex items-center justify-center overflow-hidden">{leftIcon}</div>
              </div>
            )}
            <input
              id={inputId}
              className={cn(
                inputVariants({ inputSize, error: !!errorMessage }),
                leftIcon && 'pl-10',
                rightIcon && 'pr-10',
                className
              )}
              ref={ref}
              {...props}
            />
            {rightIcon && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <div className="h-5 w-5 text-[var(--color-surface-text)] opacity-50 flex items-center justify-center overflow-hidden">{rightIcon}</div>
              </div>
            )}
          </div>
        </div>
        {errorMessage && (
          <p className="mt-1 text-sm text-[var(--color-error)]">{errorMessage}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input'; 